<template>
  <v-main>
    <v-container fluid class="pa-6">
      <!-- Summary Cards -->
      <v-row>
        <v-col cols="12" md="3" sm="6">
          <v-card class="stat-card" elevation="0">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text mb-1">Company Stock</div>
                <div class="text-h4 font-weight-bold">
                  {{ formatNumber(summary.total_company_stock) }}
                </div>
                <div class="text-caption primary--text mt-1">In warehouses</div>
              </div>
              <v-avatar size="48" color="primary lighten-4">
                <v-icon color="primary" size="24">mdi-package-variant</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3" sm="6">
          <v-card class="stat-card" elevation="0">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text mb-1">Assigned Stock</div>
                <div class="text-h4 font-weight-bold">
                  {{ formatNumber(summary.total_assigned_stock) }}
                </div>
                <div class="text-caption indigo--text mt-1">Active</div>
              </div>
              <v-avatar size="48" color="indigo lighten-4">
                <v-icon color="indigo" size="24">mdi-account-group</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3" sm="6">
          <v-card class="stat-card" elevation="0">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text mb-1">Sold Stock</div>
                <div class="text-h4 font-weight-bold">
                  {{ formatNumber(summary.total_sold) }}
                </div>
                <div class="text-caption success--text mt-1">This month</div>
              </div>
              <v-avatar size="48" color="success lighten-4">
                <v-icon color="success" size="24">mdi-trending-up</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3" sm="6">
          <v-card class="stat-card" elevation="0">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text mb-1">Available Stock</div>
                <div class="text-h4 font-weight-bold">
                  {{ formatNumber(summary.total_available) }}
                </div>
                <div class="text-caption orange--text mt-1">Ready to sell</div>
              </div>
              <v-avatar size="48" color="orange lighten-4">
                <v-icon color="orange" size="24"
                  >mdi-package-variant-closed</v-icon
                >
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters Section -->
      <v-card class="mt-6" elevation="0" style="border: 1px solid #00000033">
        <v-card-text>
          <div class="d-flex align-center mb-4">
            <v-icon class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-h6">Advanced Filters</span>
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              @click="filterPanel = filterPanel === 0 ? null : 0"
            >
              {{ filterPanel === 0 ? "Hide" : "Show" }}
            </v-btn>
          </div>
          <v-expand-transition>
            <div v-show="filterPanel === 0">
              <v-row>
                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text">User Type</label>
                    <v-select
                      style="border: 1px solid #00000033"
                      v-model="filters.user_type"
                      :items="userTypes"
                      item-text="name"
                      item-value="id"
                      placeholder="Select user type"
                      solo
                      flat
                      hide-details
                      clearable
                      class="mt-1"
                      @change="onUserTypeChange"
                    ></v-select>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text">Specific User</label>
                    <v-autocomplete
                      style="border: 1px solid #00000033"
                      v-model="filters.user_id"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      placeholder="Select user"
                      solo
                      flat
                      hide-details
                      clearable
                      class="mt-1"
                    ></v-autocomplete>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text">Product</label>
                    <v-autocomplete
                      style="border: 1px solid #00000033"
                      v-model="filters.product_id"
                      :items="products"
                      item-text="name"
                      item-value="id"
                      placeholder="Select product"
                      solo
                      flat
                      hide-details
                      clearable
                      class="mt-1"
                      @change="onProductChange"
                    ></v-autocomplete>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text"
                      >Product Variants</label
                    >
                    <v-autocomplete
                      style="border: 1px solid #00000033"
                      v-model="filters.variant_id"
                      :items="productVariants"
                      :item-text="
                        (item) => item.name + ' (' + item.product_name + ')'
                      "
                      item-value="id"
                      placeholder="Select variant"
                      solo
                      flat
                      hide-details
                      clearable
                      class="mt-1"
                    ></v-autocomplete>
                  </div>
                </v-col>
                <v-col cols="12" md="3" v-if="showLocationFilter">
                  <div class="filter-field">
                    <label class="text-caption grey--text">Location</label>
                    <v-autocomplete
                      style="border: 1px solid #00000033"
                      v-model="filters.location_id"
                      :items="locations"
                      item-text="name"
                      item-value="id"
                      placeholder="Select location"
                      solo
                      flat
                      hide-details
                      clearable
                      class="mt-1"
                    ></v-autocomplete>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text">From Date</label>
                    <v-menu
                      v-model="dateFromMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          style="border: 1px solid #00000033"
                          v-model="filters.date_from"
                          placeholder="dd-mm-yyyy"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                          solo
                          flat
                          hide-details
                          class="mt-1"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filters.date_from"
                        @input="dateFromMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="filter-field">
                    <label class="text-caption grey--text">To Date</label>
                    <v-menu
                      v-model="dateToMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          style="border: 1px solid #00000033"
                          v-model="filters.date_to"
                          placeholder="dd-mm-yyyy"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                          solo
                          flat
                          hide-details
                          class="mt-1"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="filters.date_to"
                        @input="dateToMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn text @click="clearFilters" class="mr-2">
                    <v-icon left small>mdi-close</v-icon>
                    Clear
                  </v-btn>
                  <v-btn color="primary" depressed @click="applyFilters">
                    <v-icon left small>mdi-filter</v-icon>
                    Apply Filters
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <!-- Tabs for different views -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="0" class="tab-card">
            <v-tabs
              v-model="activeTab"
              background-color="transparent"
              color="primary"
            >
              <v-tab>
                <v-icon left>mdi-account-group</v-icon>
                By User Type
              </v-tab>
              <v-tab>
                <v-icon left>mdi-package-variant</v-icon>
                By Product
              </v-tab>
              <v-tab>
                <v-icon left>mdi-map-marker</v-icon>
                Area-wise
              </v-tab>
              <v-tab>
                <v-icon left>mdi-account-star</v-icon>
                User Performance
              </v-tab>
              <v-tab>
                <v-icon left>mdi-table</v-icon>
                Detailed View
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Tab 1: Stock by User Type -->
              <v-tab-item>
                <v-card-text class="pa-6">
                  <v-row v-if="!loading && stockByUserType.length > 0">
                    <v-col
                      v-for="userType in stockByUserType"
                      :key="userType.user_type"
                      cols="12"
                      md="4"
                      class="pa-3"
                    >
                      <v-card elevation="0" class="user-type-card">
                        <v-card-text class="pa-4">
                          <div
                            class="d-flex justify-space-between align-center mb-4"
                          >
                            <div class="text-h6 font-weight-medium">
                              {{ userType.user_type }}
                            </div>
                            <v-chip small color="success" class="white--text">
                              {{ Math.round(userType.sell_through_rate || 0) }}%
                              Sold
                            </v-chip>
                          </div>

                          <div class="metrics-grid">
                            <div class="metric-row mb-3">
                              <div class="metric-col">
                                <div class="text-caption grey--text mb-1">
                                  Assigned
                                </div>
                                <div class="d-flex align-baseline">
                                  <span class="text-caption grey--text mr-2"
                                    >Sold</span
                                  >
                                  <span
                                    class="text-body-1 font-weight-medium"
                                    >{{
                                      formatNumber(userType.total_assigned)
                                    }}</span
                                  >
                                </div>
                              </div>
                              <div class="metric-col">
                                <div class="text-caption grey--text mb-1">
                                  Unsold
                                </div>
                                <div class="text-body-1 font-weight-medium">
                                  {{
                                    formatNumber(
                                      userType.total_assigned -
                                        userType.total_sold || 0
                                    )
                                  }}
                                </div>
                              </div>
                            </div>

                            <div class="metric-row mb-4">
                              <div class="metric-col">
                                <div class="text-caption grey--text mb-1">
                                  Sold
                                </div>
                                <div class="text-body-1 font-weight-medium">
                                  {{ formatNumber(userType.total_sold) }}
                                </div>
                              </div>
                              <div class="metric-col">
                                <div class="text-caption grey--text mb-1">
                                  Available
                                </div>
                                <div class="text-body-1 font-weight-medium">
                                  {{ formatNumber(userType.available || 0) }}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div>
                            <div class="text-caption grey--text mb-2">
                              Sales Progress
                            </div>
                            <v-progress-linear
                              :value="userType.sell_through_rate || 0"
                              height="4"
                              color="grey"
                              background-color="grey lighten-4"
                              rounded
                            ></v-progress-linear>
                            <div class="text-right text-caption mt-1">
                              {{ Math.round(userType.sell_through_rate || 0) }}%
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row v-else-if="!loading && stockByUserType.length === 0">
                    <v-col cols="12" class="text-center py-8">
                      <v-icon size="64" color="grey lighten-2"
                        >mdi-package-variant-closed</v-icon
                      >
                      <h3 class="grey--text mt-4">No stock data available</h3>
                      <p class="grey--text text--lighten-1">
                        Try adjusting your filters or check back later
                      </p>
                    </v-col>
                  </v-row>
                  <v-progress-linear
                    v-if="loading"
                    indeterminate
                    color="primary"
                  ></v-progress-linear>
                </v-card-text>
              </v-tab-item>

              <!-- Tab 2: Product-wise Analytics -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <v-data-table
                    :headers="productHeaders"
                    :items="productAnalytics"
                    :loading="loading"
                    class="striped-table"
                    :items-per-page="10"
                  >
                    <template v-slot:[`item.product_name`]="{ item }">
                      <div>
                        <div class="font-weight-medium">
                          {{ item.product_name }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ item.category }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.performance_score`]="{ item }">
                      <v-chip
                        :color="getPerformanceColor(item.performance_score)"
                        dark
                        small
                      >
                        {{ item.performance_score }}%
                      </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewProductDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Tab 3: Area-wise Distribution -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <v-row>
                    <v-col cols="12">
                      <v-data-table
                        :headers="areaHeaders"
                        :items="areaWiseDistribution"
                        :loading="loading"
                        class="striped-table"
                        :items-per-page="10"
                      >
                        <template v-slot:[`item.city_name`]="{ item }">
                          <div class="d-flex align-center">
                            <v-icon small class="mr-2">mdi-map-marker</v-icon>
                            {{ item.city_name }}
                          </div>
                        </template>

                        <template v-slot:[`item.metrics`]="{ item }">
                          <div class="d-flex align-center">
                            <div class="text-center mr-4">
                              <div class="text-caption grey--text">Users</div>
                              <div class="font-weight-bold">
                                {{ item.user_count }}
                              </div>
                            </div>
                            <div class="text-center mr-4">
                              <div class="text-caption grey--text">
                                Products
                              </div>
                              <div class="font-weight-bold">
                                {{ item.product_count }}
                              </div>
                            </div>
                          </div>
                        </template>

                        <template v-slot:[`item.stock_info`]="{ item }">
                          <div>
                            <div>
                              Assigned:
                              <strong>{{
                                formatNumber(item.total_assigned)
                              }}</strong>
                            </div>
                            <div>
                              Sold:
                              <strong class="success--text">{{
                                formatNumber(item.total_sold)
                              }}</strong>
                            </div>
                            <div>
                              Available:
                              <strong class="warning--text">{{
                                formatNumber(item.available)
                              }}</strong>
                            </div>
                          </div>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-tab-item>

              <!-- Tab 4: User Performance -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <v-data-table
                    :headers="userPerformanceHeaders"
                    :items="userPerformance"
                    :loading="loading"
                    class="striped-table"
                    :items-per-page="10"
                    :sort-by="['performance_score']"
                    :sort-desc="[true]"
                  >
                    <template v-slot:[`item.user_info`]="{ item }">
                      <div>
                        <div class="font-weight-medium">
                          {{ item.user_name }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ item.position }} | {{ item.location }}
                        </div>
                        <div class="text-caption">{{ item.email }}</div>
                      </div>
                    </template>

                    <template v-slot:[`item.stock_metrics`]="{ item }">
                      <div>
                        <v-chip x-small class="ma-1"
                          >Products: {{ item.total_products }}</v-chip
                        >
                        <v-chip x-small class="ma-1"
                          >Variants: {{ item.total_variants }}</v-chip
                        >
                        <v-chip x-small class="ma-1"
                          >Qty:
                          {{
                            formatNumber(item.total_assigned_quantity)
                          }}</v-chip
                        >
                      </div>
                    </template>

                    <template v-slot:[`item.sales_metrics`]="{ item }">
                      <div>
                        <div>
                          Orders: <strong>{{ item.total_orders }}</strong>
                        </div>
                        <div>
                          Revenue:
                          <strong class="success--text"
                            >₹{{
                              formatNumber(item.total_sales_amount)
                            }}</strong
                          >
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.performance_score`]="{ item }">
                      <v-progress-circular
                        :rotate="-90"
                        :size="50"
                        :width="5"
                        :value="item.performance_score"
                        :color="getPerformanceColor(item.performance_score)"
                      >
                        {{ item.performance_score }}
                      </v-progress-circular>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewUserDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Tab 5: Detailed Stock View -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <v-row class="mb-3">
                    <v-col
                      cols="12"
                      class="d-flex justify-space-between align-center"
                    >
                      <div>
                        <v-chip class="mr-2" color="primary">
                          Total Assigned:
                          {{ formatNumber(detailedSummary.total_assigned) }}
                        </v-chip>
                        <v-chip class="mr-2" color="success">
                          Total Sold:
                          {{ formatNumber(detailedSummary.total_sold) }}
                        </v-chip>
                        <v-chip color="warning">
                          Available:
                          {{ formatNumber(detailedSummary.total_available) }}
                        </v-chip>
                      </div>
                      <v-btn color="success" @click="exportToExcel" rounded>
                        <v-icon left>mdi-microsoft-excel</v-icon>
                        Export to Excel
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-data-table
                    :headers="detailedHeaders"
                    :items="detailedStockData"
                    :loading="loading"
                    class="striped-table"
                    :items-per-page="20"
                    :server-items-length="totalItems"
                    :options.sync="options"
                    @update:options="loadDetailedStock"
                  >
                    <template v-slot:[`item.user`]="{ item }">
                      <div>
                        <div class="font-weight-medium">
                          {{ item.assignedTo.first_name }}
                          {{ item.assignedTo.last_name }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ item.assignedTo.position?.name }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.product`]="{ item }">
                      <div>
                        <div>{{ item.product.name }}</div>
                        <div class="text-caption grey--text">
                          {{ item.variant?.name }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.location`]="{ item }">
                      <div class="text-caption">
                        <div>{{ item.assignedTo.location?.name }}</div>
                        <div class="grey--text">
                          {{ item.assignedTo.city_name }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.stock_status`]="{ item }">
                      <div>
                        <v-chip x-small color="info" class="ma-1"
                          >Assigned: {{ item.quantity }}</v-chip
                        >
                        <v-chip x-small color="success" class="ma-1"
                          >Sold: {{ item.sold_quantity }}</v-chip
                        >
                        <v-chip x-small color="warning" class="ma-1"
                          >Available: {{ item.available_quantity }}</v-chip
                        >
                      </div>
                    </template>

                    <template v-slot:[`item.sell_percentage`]="{ item }">
                      <v-progress-linear
                        :value="item.sell_percentage"
                        height="20"
                        :color="getProgressColor(item.sell_percentage)"
                      >
                        {{ item.sell_percentage }}%
                      </v-progress-linear>
                    </template>

                    <template v-slot:[`item.created_at`]="{ item }">
                      {{ item.created_at | niceDate }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>

      <!-- Low Stock Alerts -->
      <v-row class="mt-4" v-if="lowStockAlerts.length > 0">
        <v-col cols="12">
          <v-card elevation="0" class="alert-card" color="warning lighten-5">
            <v-card-title class="warning--text">
              <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
              Low Stock Alerts ({{ lowStockAlerts.length }})
            </v-card-title>
            <v-card-text>
              <v-chip-group column>
                <v-chip
                  v-for="(alert, index) in lowStockAlerts"
                  :key="index"
                  :color="
                    alert.alert_level === 'critical' ? 'error' : 'warning'
                  "
                  small
                  outlined
                  class="ma-1"
                >
                  {{ alert.product_name }} - {{ alert.variant_name }} ({{
                    alert.current_quantity
                  }}
                  left)
                  <span v-if="alert.user_name"> - {{ alert.user_name }}</span>
                </v-chip>
              </v-chip-group>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Top Selling Products -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="0" class="products-card">
            <v-card-title>
              <v-icon class="mr-2" color="success">mdi-trending-up</v-icon>
              <span class="font-weight-bold">Top Selling Products</span>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="(product, index) in topSellingProducts.slice(0, 6)"
                  :key="index"
                  cols="12"
                  md="4"
                >
                  <v-card elevation="2" class="pa-3 product-card">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div>
                        <div class="font-weight-medium">
                          {{ product.product_name }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ product.category }}
                        </div>
                      </div>
                      <v-avatar color="primary" size="30">
                        <span class="white--text text-caption">{{
                          index + 1
                        }}</span>
                      </v-avatar>
                    </div>

                    <div class="d-flex justify-space-between text-caption">
                      <div>
                        <div class="grey--text">Sold</div>
                        <div class="font-weight-bold">
                          {{ formatNumber(product.total_sold) }}
                        </div>
                      </div>
                      <div>
                        <div class="grey--text">Revenue</div>
                        <div class="font-weight-bold success--text">
                          ₹{{ formatNumber(product.revenue) }}
                        </div>
                      </div>
                      <div>
                        <div class="grey--text">Rate</div>
                        <div class="font-weight-bold">
                          {{ product.sell_through_rate }}%
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="product.top_performer"
                      class="mt-2 pa-2 grey lighten-4 rounded"
                    >
                      <div class="text-caption">
                        <v-icon x-small>mdi-star</v-icon>
                        Top Performer: {{ product.top_performer.name }} ({{
                          product.top_performer.quantity_sold
                        }}
                        units)
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "AdminStockTracking",

  data() {
    return {
      loading: false,
      activeTab: 0,
      filterPanel: null,
      dateFromMenu: false,
      dateToMenu: false,

      // Filters
      filters: {
        user_type: "",
        user_id: "",
        product_id: "",
        variant_id: "",
        location_id: "",
        date_from: "",
        date_to: "",
        city_id: "",
        state_id: "",
      },

      // Dashboard data
      summary: {
        total_company_stock: 0,
        total_assigned_stock: 0,
        total_sold: 0,
        total_available: 0,
        total_users_with_stock: 0,
        stock_utilization_rate: 0,
      },

      stockByUserType: [],
      productAnalytics: [],
      areaWiseDistribution: [],
      userPerformance: [],
      topSellingProducts: [],
      lowStockAlerts: [],
      detailedStockData: [],
      detailedSummary: {
        total_assigned: 0,
        total_sold: 0,
        total_available: 0,
      },

      // Master data for dropdowns
      userTypes: [], // Will be loaded from positions API
      positions: [], // Store positions data
      allUsers: [], // Store all users before filtering
      users: [], // Filtered users based on selected position
      products: [],
      allProductVariants: [], // Store all variants before filtering
      productVariants: [], // Filtered variants based on selected product
      locations: [],

      // Table configurations
      options: {},
      totalItems: 0,

      // Table headers
      productHeaders: [
        { text: "Product", value: "product_name", sortable: true },
        { text: "Category", value: "category", sortable: true },
        { text: "Company Stock", value: "company_stock", sortable: true },
        { text: "Assigned", value: "total_assigned", sortable: true },
        { text: "Sold", value: "total_sold", sortable: true },
        { text: "Available", value: "available_stock", sortable: true },
        { text: "Performance", value: "performance_score", sortable: true },
        { text: "Actions", value: "actions", sortable: false },
      ],

      areaHeaders: [
        { text: "City", value: "city_name", sortable: true },
        { text: "Metrics", value: "metrics", sortable: false },
        { text: "Stock Info", value: "stock_info", sortable: false },
      ],

      userPerformanceHeaders: [
        { text: "User Info", value: "user_info", sortable: false },
        { text: "Stock Metrics", value: "stock_metrics", sortable: false },
        { text: "Sales Metrics", value: "sales_metrics", sortable: false },
        { text: "Performance", value: "performance_score", sortable: true },
        { text: "Actions", value: "actions", sortable: false },
      ],

      detailedHeaders: [
        { text: "User", value: "user", sortable: false },
        { text: "Product", value: "product", sortable: false },
        { text: "Location", value: "location", sortable: false },
        { text: "Stock Status", value: "stock_status", sortable: false },
        { text: "Sell %", value: "sell_percentage", sortable: true },
        { text: "Date", value: "created_at", sortable: true },
      ],
    };
  },

  computed: {
    activeFiltersCount() {
      return Object.values(this.filters).filter((v) => v && v !== "").length;
    },
    showLocationFilter() {
      // Show location field only for DISTRIBUTOR/DEALER position
      if (!this.filters.user_type) return false;
      const selectedPosition = this.positions.find(
        (p) => p.id === this.filters.user_type
      );
      return (
        selectedPosition &&
        (selectedPosition.name === "DISTRIBUTOR/DEALER" ||
          selectedPosition.name.includes("DISTRIBUTOR"))
      );
    },
  },

  mounted() {
    this.loadDashboard();
    this.loadMasterData();
  },

  methods: {
    loadDashboard() {
      this.loading = true;

      // Build query string with only non-empty filters
      const queryParams = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "") {
          queryParams[key] = this.filters[key];
        }
      });

      axios
        .get("admin/stock-tracking/dashboard", {
          params: queryParams,
        })
        .then((response) => {
          if (response.data && response.data.success) {
            const data = response.data.data;
            this.summary = data.summary || this.summary;
            this.stockByUserType = data.stock_by_user_type || [];
            this.areaWiseDistribution = data.area_wise_distribution || [];
            this.topSellingProducts = data.top_selling_products || [];
            this.lowStockAlerts = data.low_stock_alerts || [];

            // Load other tab data based on active tab
            if (this.activeTab > 0) {
              this.loadTabData();
            }
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading dashboard:", error);
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            alert("Error: " + error.response.data.message);
          }
          this.loading = false;
        });
    },

    loadTabData() {
      switch (this.activeTab) {
        case 1: // Product Analytics
          this.loadProductAnalytics();
          break;
        case 3: // User Performance
          this.loadUserPerformance();
          break;
        case 4: // Detailed View
          this.loadDetailedStock();
          break;
      }
    },

    loadProductAnalytics() {
      this.loading = true;

      // Build query string with only non-empty filters
      const queryParams = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "") {
          queryParams[key] = this.filters[key];
        }
      });

      axios
        .get("admin/stock-tracking/product-analytics", {
          params: queryParams,
        })
        .then((response) => {
          if (response.data && response.data.success) {
            this.productAnalytics = response.data.data || [];
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading product analytics:", error);
          this.loading = false;
        });
    },

    loadUserPerformance() {
      this.loading = true;

      // Build query string with only non-empty filters
      const queryParams = { sort_by_performance: true };
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "") {
          queryParams[key] = this.filters[key];
        }
      });

      axios
        .get("admin/stock-tracking/user-performance", {
          params: queryParams,
        })
        .then((response) => {
          if (response.data && response.data.success) {
            this.userPerformance = response.data.data || [];
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading user performance:", error);
          this.loading = false;
        });
    },

    loadDetailedStock() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      // Build query string with only non-empty filters
      const queryParams = {
        page: page || 1,
        per_page: itemsPerPage || 20,
        sort_by: sortBy ? sortBy[0] : null,
        sort_order: sortDesc ? (sortDesc[0] ? "desc" : "asc") : "desc",
      };

      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "") {
          queryParams[key] = this.filters[key];
        }
      });

      axios
        .get("admin/stock-tracking/detailed", {
          params: queryParams,
        })
        .then((response) => {
          if (response.data && response.data.success) {
            this.detailedStockData = response.data.data || [];
            this.totalItems = response.data.pagination
              ? response.data.pagination.total
              : 0;
            this.detailedSummary = response.data.summary || {};
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading detailed stock:", error);
          this.loading = false;
        });
    },

    loadMasterData() {
      // Load positions first
      axios
        .get("positions")
        .then((response) => {
          if (response.data && response.data.data) {
            this.positions = response.data.data.filter(
              (pos) => pos.is_active && !pos.is_deleted
            );
            this.userTypes = this.positions.map((position) => ({
              id: position.id,
              name: position.name,
            }));
          }
        })
        .catch((error) => {
          console.error("Error loading positions:", error);
        });

      // Load users with pagination
      axios
        .get("users", {
          params: { page: 1, rowsPerPage: 1000 },
        })
        .then((response) => {
          if (response.data && response.data.data) {
            this.allUsers = response.data.data.map((user) => ({
              id: user.id,
              name: `${user.first_name || ""} ${user.last_name || ""} ${
                user.position ? "(" + user.position.name + ")" : ""
              }`.trim(),
              position_id: user.position_id,
            }));
            this.users = [...this.allUsers]; // Initialize users with all users
          }
        })
        .catch((error) => {
          console.error("Error loading users:", error);
        });

      // Load products
      axios
        .get("products")
        .then((response) => {
          if (response.data && response.data.data) {
            this.products = response.data.data.map((product) => ({
              id: product.id,
              name: product.name,
            }));
          }
        })
        .catch((error) => {
          console.error("Error loading products:", error);
        });

      // Load product variants
      axios
        .get("products_variants")
        .then((response) => {
          if (response.data && response.data.data) {
            this.allProductVariants = response.data.data.map((variant) => ({
              id: variant.id,
              name: variant.name || "Default",
              product_name: variant.product ? variant.product.name : "",
              product_id: variant.product_id,
            }));
            this.productVariants = [...this.allProductVariants]; // Initialize with all variants
          }
        })
        .catch((error) => {
          console.error("Error loading product variants:", error);
        });

      // Load locations - ensure we're getting the correct structure
      axios
        .get("/locations")
        .then((response) => {
          if (response.data && response.data.data) {
            // If locations are coming as plain objects, use them directly
            if (Array.isArray(response.data.data)) {
              this.locations = response.data.data.map((location) => {
                // Handle both object and simple formats
                if (typeof location === "object" && location !== null) {
                  // Build location name from city and address
                  const cityName = location.city?.description || "";
                  const stateName = location.state?.description || "";
                  const address = location.address || "";

                  // Create a descriptive name combining available fields
                  let displayName = "";
                  if (cityName) {
                    displayName = cityName;
                    if (address) {
                      displayName = `${address}, ${cityName}`;
                    }
                    if (stateName) {
                      displayName += ` (${stateName})`;
                    }
                  } else if (address) {
                    displayName = address;
                  } else {
                    displayName = "Unknown Location";
                  }

                  return {
                    id: location.id,
                    name: displayName,
                  };
                }
                return location;
              });
            } else {
              this.locations = [];
            }
          }
        })
        .catch((error) => {
          console.error("Error loading locations:", error);
        });
    },

    applyFilters() {
      this.loadDashboard();
    },

    clearFilters() {
      this.filters = {
        user_type: "",
        user_id: "",
        product_id: "",
        variant_id: "",
        location_id: "",
        date_from: "",
        date_to: "",
        city_id: "",
        state_id: "",
      };
      this.loadDashboard();
    },

    exportToExcel() {
      // Build query string with only non-empty filters
      const queryParams = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "") {
          queryParams[key] = this.filters[key];
        }
      });

      axios
        .get("admin/stock-tracking/export", {
          params: queryParams,
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `stock_tracking_${new Date().getTime()}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch((error) => {
          console.error("Error exporting data:", error);
          alert("Error exporting data. Please try again.");
        });
    },

    viewProductDetails(product) {
      // Navigate to product details or open dialog
      this.$router.push(`/products/${product.product_id}`);
    },

    viewUserDetails(user) {
      // Navigate to user details or open dialog
      this.$router.push(`/users/${user.user_id}`);
    },

    formatNumber(value) {
      if (!value) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getProgressColor(value) {
      if (value >= 75) return "success";
      if (value >= 50) return "info";
      if (value >= 25) return "warning";
      return "error";
    },

    getPerformanceColor(value) {
      if (value >= 80) return "success";
      if (value >= 60) return "primary";
      if (value >= 40) return "warning";
      return "error";
    },

    onUserTypeChange() {
      // Filter users based on selected position
      if (this.filters.user_type) {
        this.users = this.allUsers.filter(
          (user) => user.position_id === this.filters.user_type
        );
      } else {
        this.users = [...this.allUsers]; // Reset to all users
      }
      // Clear selected user when position changes
      this.filters.user_id = "";

      // Clear location if position doesn't support it
      if (!this.showLocationFilter) {
        this.filters.location_id = "";
      }
    },

    onProductChange() {
      // Filter variants based on selected product
      if (this.filters.product_id) {
        this.productVariants = this.allProductVariants.filter(
          (variant) => variant.product_id === this.filters.product_id
        );
      } else {
        this.productVariants = [...this.allProductVariants]; // Reset to all variants
      }
      // Clear selected variant when product changes
      this.filters.variant_id = "";
    },
  },

  watch: {
    activeTab() {
      this.loadTabData();
    },
  },
};
</script>

<style scoped>
/* Modern card styling */
.stat-card {
  border-radius: 12px !important;
  border: 1px solid #00000033;
  transition: all 0.3s ease;
  background: white;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
}

/* Tab card */
.tab-card {
  border-radius: 12px !important;
  border: 1px solid #e8e8e8;
}

/* User type cards in tabs */
.user-type-card {
  border-radius: 12px !important;
  border: 1px solid #e8e8e8;
  min-height: 250px;
  transition: all 0.3s ease;
  background: white;
}

.user-type-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-2px);
}

/* Metrics grid */
.metrics-grid {
  width: 100%;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.metric-col {
  flex: 1;
  padding-right: 16px;
}

.metric-col:last-child {
  padding-right: 0;
}

/* Product and alert cards */
.products-card,
.alert-card {
  border-radius: 12px !important;
  border: 1px solid #e8e8e8;
}

.product-card {
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Filter section styling */
.filter-field {
  margin-bottom: 8px;
}

.filter-field label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #757575;
}

.filter-field .v-input {
  margin-top: 4px !important;
}

/* Tab styling */
.v-tabs {
  border-bottom: 1px solid #e0e0e0;
}

.v-tab {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0;
}

/* Progress bar styling */
.v-progress-linear {
  border-radius: 4px !important;
}

/* Data table styling */
.striped-table tbody tr:nth-child(even) {
  background-color: #fafafa;
}

.striped-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

/* Chip styling */
.v-chip {
  font-weight: 600;
  letter-spacing: 0.25px;
}

/* Text styling */
.text-h4 {
  font-weight: 700 !important;
  line-height: 1.2;
}

.text-h6 {
  font-weight: 600 !important;
  color: #333;
}

.text-body-1 {
  font-size: 1rem !important;
}

/* Clean input field styling */
.v-text-field--solo.v-input--is-focused > .v-input__control > .v-input__slot {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.v-text-field--solo > .v-input__control > .v-input__slot {
  background: #fafafa !important;
  border: 1px solid #e0e0e0;
}

.v-select--is-menu-active > .v-input__control > .v-input__slot {
  background: white !important;
}

/* Button styling */
.v-btn--depressed {
  box-shadow: none !important;
}

.v-btn--text {
  text-transform: none !important;
  letter-spacing: 0;
}

/* Alert styling */
.v-alert {
  border-radius: 8px !important;
}

/* Avatar icon backgrounds */
.v-avatar {
  border-radius: 12px !important;
}

/* Consistent spacing */
.pa-6 {
  padding: 24px !important;
}

/* Divider styling */
.v-divider {
  opacity: 0.12;
}
</style>
